import React, { useEffect, useMemo, useState } from "react";
import {
  CButton,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilEnvelopeClosed } from "@coreui/icons";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";

import validationHelper from "@/helpers/validationHelper";
import {
  useUserLogin,
} from "@/hooks";
import { UserService } from "@/api";
import {
  addUser,
  loggedUser,
} from "@/store/features/userSlice";
import { auth, dashboard } from "@/libs/routes";
import GuestLayout from "@/layout/GuestLayout";
import { useTranslationContext } from "@/context/TranslationContext";
import { sign_in } from "@/constants/translation/auth.translation";
import { session_auto_logout } from "@/constants/translation/common.translation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeLowVision } from "@fortawesome/free-solid-svg-icons";

/**
 * @name SignIn
 *
 * @description
 * The SignIn component provides a form for users to log in to their accounts. It includes
 * fields for email and password, and a button to submit the login credentials.
 *
 * If the user is successfully authenticated, the user data is stored in Redux state, and
 * the user is redirected to the dashboard. If authentication fails, an error message is shown.
 *
 * API Response Format:
 * {
 *   success: true,
 *   message: "backend_login_success",
 *   data: {
 *     token: "eyJhbGciOiJIUzI1NiIs...",
 *     encodedToken: "0b89f5f1-2787-4480-8988-4.....",
 *     user: { id, uuid, full_name, email, profile_photo_url, is_admin }
 *   }
 * }
 *
 * @returns {JSX.Element} The JSX for rendering the login page.
 */
const SignIn = () => {
  const t = useTranslationContext(); // Fetching translation context for multi-language support
  const dispatch = useDispatch(); // Dispatch to update the Redux store with user data
  const navigate = useNavigate();
  const checkLoggenInUser = useSelector(loggedUser); // Check if the user is logged in using Redux state
  const sessionTimeout = localStorage.getItem("session_timeout");

  // Memoize validation schema to avoid recalculating it on every render
  const validationSchema = useMemo(
    () => validationHelper.signInValidation(),
    []
  );
  
  // Mutation hook for the login API request
  const { mutate: doLogin, isPending: isLoading } = useUserLogin(
    async (response) => {
      toast.success(response.message); // Show success message on successful login
      loginForm.resetForm(); // Reset the form
      
      // API returns: { data: { token, user: { ... } } }
      const dataStore = {
        userData: response.data.user, // API returns user inside 'user' not 'userData'
        isLoggedIn: true,
        accessToken: response.data.accessToken, // API returns token inside 'data'
      };

      dispatch(addUser(dataStore)); // Store user data in Redux
      
      // Immediately fetch fresh profile data to get the latest profile picture
      try {
        const profileResponse = await UserService.getProfile();
        if (profileResponse?.data) {
          // The profile data is already decrypted from API
          const userData = profileResponse.data.user; // Access the nested user object
          
          dispatch(addUser({
            userData: userData,
            accessToken: response.data.accessToken
          }));
        }
      } catch (error) {
        console.error('Error fetching profile after login:', error);
        // Continue with login even if profile fetch fails
      }
      
      navigate(dashboard); // Redirect to dashboard after successful login
    },
    (error) => {
      let message = t(sign_in.general_error);
      
      // Check for 401 status code (authentication failed)
      if (error?.response?.status === 401) {
        message = t(sign_in.incorrect_credentials_error);
        // Clear the password field to prevent multiple failed attempts
        loginForm.setFieldValue('password', '');
      } else {
        // Use backend message if available, otherwise fallback to error message
        message = error?.response?.data?.message || error?.message || message;
      }
      
      toast.error(message); // Show error message if login fails
    }
  );

  // Formik setup to manage form state and validation
  const loginForm = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema,
    onSubmit: (values) => {
      doLogin(values);
    }, // Call the login mutation on form submit
  });

  // Redirect to forgot password page
  const redirectToForgotPassword = () => navigate(auth.forgotPassword);

  // Check if the user is logged in, if true, redirect to the dashboard
  useEffect(() => {
    if (checkLoggenInUser?.isLoggedIn) {
      navigate(dashboard);
    }
  }, [checkLoggenInUser, navigate]);

  useEffect(() => {
    if (sessionTimeout) {
      toast.error(t(session_auto_logout));
    }
    localStorage.removeItem("session_timeout");
  }, [sessionTimeout, t]);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  return (
    <>
      {/* Login Form */}
      <CForm onSubmit={loginForm.handleSubmit}>
        <h1>{t(sign_in.main_heading)}</h1>
        <p>{t(sign_in.sub_heading)}</p>

        {/* Email Input */}
        <div className="mb-3">
          <CInputGroup>
            <CInputGroupText>
              <CIcon icon={cilEnvelopeClosed} />
            </CInputGroupText>
            <CFormInput
              placeholder={t(sign_in.email_placeholder)}
              autoComplete="email"
              type="email"
              name="email"
              className="form-control same-input-cls"
              onChange={loginForm.handleChange}
              onBlur={loginForm.handleBlur}
              value={loginForm.values.email}
            />
          </CInputGroup>
          {loginForm.errors.email && (
            <div className="error text-danger mt-1">
              {t(loginForm.errors.email)}
            </div>
          )}
        </div>

        {/* Password Input */}
        <div className="mb-4">
          <CInputGroup>
            <CInputGroupText onClick={togglePasswordVisibility}>
              <FontAwesomeIcon icon={showPassword ? faEye : faEyeLowVision} />{" "}
            </CInputGroupText>
            <CFormInput
              type={"password" && showPassword ? "text" : "password"}
              placeholder={t(sign_in.password_placeholder)}
              autoComplete="current-password"
              name="password"
              className="form-control same-input-cls"
              onChange={loginForm.handleChange}
              onBlur={loginForm.handleBlur}
              value={loginForm.values.password}
            />
          </CInputGroup>
          {loginForm.touched.password && loginForm.errors.password && (
            <div className="error text-danger mt-1">
              {t(loginForm.errors.password)}
            </div>
          )}
        </div>

        {/* Submit and Forgot Password */}
        <div className="d-flex gap-3 mt-4">
          <CButton
            color="primary"
            className="px-3 w-40 d-flex gap-6 align-items-center justify-content-around"
            type="submit"
            disabled={isLoading}
          >
            {isLoading && <CSpinner size="sm" variant="grow" />}
            {t(sign_in.login_btn_text)}
          </CButton>
          <div className="d-flex align-items-center ms-4">
            <Link 
              to={auth.forgotPassword}
              className="forgot-password-link"
            >
              {t(sign_in.forgot_password_link_text)}
            </Link>
          </div>
        </div>
      </CForm>
    </>
  );
};

/**
 * @name getLayout
 *
 * @description
 * Wraps the SignIn page with the GuestLayout component to provide a consistent layout
 * across the login pages.
 *
 * @returns {JSX.Element} The JSX layout for the page.
 */
SignIn.getLayout = function getLayout(page) {
  return <GuestLayout>{page}</GuestLayout>;
};

export default SignIn;
